import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { signIn } from "@/lib/auth-client";

function GoogleMark() {
  return (
    <svg
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      className="h-[22px] w-[22px]"
      role="img"
      aria-label="Google"
    >
      <title>Google</title>
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.6 16.1 19 13 24 13c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.3c-2 1.4-4.5 2.5-7.3 2.5-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.6 39.7 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.7l6.2 5.3C40.9 36.1 44 30.5 44 24c0-1.3-.1-2.6-.4-3.5z"
      />
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg
      viewBox="0 0 23 23"
      xmlns="http://www.w3.org/2000/svg"
      className="h-[22px] w-[22px]"
      role="img"
      aria-label="Microsoft"
    >
      <title>Microsoft</title>
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

function SsoButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="group grid w-full grid-cols-[22px_1fr_16px] items-center gap-3.5 rounded-[10px] border border-input bg-white px-[18px] py-4 text-left text-sm font-semibold text-foreground transition-[border,box-shadow,transform] duration-150 hover:-translate-y-px hover:border-ring hover:shadow-[0_6px_18px_color-mix(in_oklab,var(--ring)_12%,transparent)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:border-input disabled:hover:shadow-none"
    >
      {icon}
      <span>{label}</span>
      <ArrowRight className="h-4 w-4 text-muted-foreground transition-[transform,color] duration-150 group-hover:translate-x-[3px] group-hover:text-[var(--ring)]" />
    </button>
  );
}

export function SsoButtons() {
  const [googlePending, setGooglePending] = useState(false);
  const [microsoftPending, setMicrosoftPending] = useState(false);

  async function continueWithGoogle() {
    setGooglePending(true);
    // On success the browser is redirected to Google then back to `callbackURL`,
    // so this only returns here on a failure to *start* the flow.
    const { error } = await signIn.social({
      provider: "google",
      callbackURL: "/dashboard",
      errorCallbackURL: "/login",
    });
    if (error) {
      setGooglePending(false);
      toast.error(error.message ?? "Couldn't start Google sign-in. Use your @nymbl.app account.");
    }
  }

  async function continueWithMicrosoft() {
    setMicrosoftPending(true);
    // On success the browser is redirected to Microsoft then back to
    // `callbackURL`, so this only returns here on a failure to *start* the flow.
    const { error } = await signIn.social({
      provider: "microsoft",
      callbackURL: "/dashboard",
      errorCallbackURL: "/login",
    });
    if (error) {
      setMicrosoftPending(false);
      toast.error(
        error.message ?? "Couldn't start Microsoft sign-in. Use your @nymbl.app account.",
      );
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <SsoButton
        icon={<GoogleMark />}
        label={googlePending ? "Redirecting to Google…" : "Continue with Google"}
        onClick={continueWithGoogle}
        disabled={googlePending}
      />
      <SsoButton
        icon={<MicrosoftMark />}
        label={microsoftPending ? "Redirecting to Microsoft…" : "Continue with Microsoft Azure"}
        onClick={continueWithMicrosoft}
        disabled={microsoftPending}
      />
    </div>
  );
}
